import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/I18nContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowRight, Key } from 'lucide-react';
import QoldoLogo from '../components/QoldoLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Login() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [authMode, setAuthMode] = useState(null); // null = loading, 'dev' or 'magic'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check auth mode on mount
  useEffect(() => {
    checkAuthMode();
    
    // Check for magic link token in URL
    const token = searchParams.get('token');
    if (token) {
      handleMagicLinkLogin(token);
    }
  }, [searchParams]);

  const checkAuthMode = async () => {
    try {
      const response = await api.get('/auth/mode');
      setAuthMode(response.data.mode || 'dev');
    } catch (error) {
      // Default to magic mode for safety in production
      setAuthMode('magic');
    }
  };

  const handleMagicLinkLogin = async (token) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/magic/verify', { token });
      const { token: jwtToken, ...userData } = response.data;
      login(userData, jwtToken);
      toast.success(`Welcome, ${userData.username || userData.email}!`);
      navigate('/');
    } catch (error) {
      console.error('Magic link login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  // Dev mode login (simple username) - ONLY works when AUTH_MODE=dev
  const handleDevLogin = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.error(t('login_username_placeholder'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/dev', { username: username.trim() });
      const { token, ...userData } = response.data;
      login(userData, token);
      toast.success(`Welcome, ${userData.username}!`);
      navigate('/');
    } catch (error) {
      console.error('Login failed:', error);
      // If dev login is disabled, show appropriate message
      if (error.response?.status === 403) {
        toast.error(t('login_error_not_allowed'));
        setAuthMode('magic'); // Force switch to magic mode
      } else {
        toast.error('Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Magic mode: send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t('login_email_label'));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/magic/send', { email: email.trim().toLowerCase() });
      setOtpSent(true);
      setPendingEmail(email.trim().toLowerCase());
      toast.success(t('login_code_sent'));
    } catch (error) {
      console.error('Failed to send OTP:', error);
      const message = error.response?.data?.detail || 'Could not send code';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Magic mode: verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error(t('login_code_label'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/magic/verify-otp', { 
        email: pendingEmail,
        otp: otp.trim() 
      });
      const { token, ...userData } = response.data;
      login(userData, token);
      toast.success(`Welcome, ${userData.username || userData.email}!`);
      navigate('/');
    } catch (error) {
      console.error('OTP verification failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking auth mode
  if (authMode === null) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <QoldoLogo className="mb-6" />
            <p className="text-gray-500">{t('loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render dev mode login (only when AUTH_MODE=dev)
  if (authMode === 'dev') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card className="w-full max-w-md" data-testid="login-card">
          <CardHeader className="text-center space-y-4">
            <QoldoLogo />
            <p className="text-sm text-gray-600">{t('app_tagline')}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDevLogin} className="space-y-4">
              <div>
                <Input
                  data-testid="username-input"
                  type="text"
                  placeholder={t('login_username_placeholder')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  className="w-full"
                />
              </div>
              <Button
                data-testid="login-button"
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? t('login_logging_in') : t('login_button')}
              </Button>
            </form>
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              <strong>⚠️</strong> {t('login_dev_mode_warning')}
            </div>
            
            {/* Language switcher */}
            <div className="mt-4 flex justify-center">
              <LanguageSwitcher />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Render magic mode login (default for production)
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center space-y-4">
          <QoldoLogo />
          <p className="text-sm text-gray-600">{t('app_tagline')}</p>
        </CardHeader>
        <CardContent>
          {!otpSent ? (
            // Step 1: Enter email
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t('login_email_label')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="email-input"
                    type="email"
                    placeholder={t('login_email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10"
                  />
                </div>
              </div>
              <Button
                data-testid="send-otp-button"
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? t('login_sending') : t('login_send_code')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-gray-500 text-center">
                {t('login_code_info')}
              </p>
              
              {/* Language switcher */}
              <div className="pt-2 flex justify-center">
                <LanguageSwitcher />
              </div>
            </form>
          ) : (
            // Step 2: Enter OTP
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">
                  {t('login_enter_code_help').split('{email}')[0]}
                  <br />
                  <strong>{pendingEmail}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('login_code_label')}</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    data-testid="otp-input"
                    type="text"
                    placeholder="123456"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    disabled={loading}
                    className="w-full pl-10 text-center text-xl tracking-widest"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
              </div>
              <Button
                data-testid="verify-otp-button"
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? t('login_verifying') : t('login_verify')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                }}
                className="w-full"
              >
                {t('login_use_other_email')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
